// SCR-STAFF-300 — تقویم، نمای روز (US-SCH-060؛ IA §2.3 `/calendar?view=day&date=1405-07-09&by=provider`): ستون به ازای ارائه‌دهنده
// یا اتاق، بلوک‌های مرخصی، تعطیل و خارج از برنامه خاکستری، کلیک روی زمان خالی ← دیالوگ رزرو پیش‌پر (تاریخ، ساعت، ارائه‌دهنده یا
// اتاق)، کلیک روی نوبت ← جزئیات (SCR-STAFF-303). به‌روزرسانی زنده با SignalR.
// AP-MED-03 (BR-SCH-126): بلوک ارجاعی با نوار رنگی (پیش‌فرض زرد) از زمان عادی جدا می‌شود؛ رزرو در آن کد ارجاع
// ملی می‌خواهد و خودِ زمان همان‌جا کلیک‌پذیر می‌ماند (PHX-SCH-0332 راهنمای کاربر است).
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { unwrap, type CalendarDayView, type CalendarMonthDay, type CalendarMonthView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, LoadingState, PageHeader, statusColors, toGregorian } from '@phoenix/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import {
  addDays,
  appointmentPath,
  blockColor,
  BranchRequired,
  DepositPaymentChip,
  formatDay,
  formatTime,
  fromUrlDate,
  IRANIAN_WEEK,
  schKeys,
  statusTone,
  toUrlDate,
  useActiveBranch,
  useBookingOptions,
  weekdayOf,
} from './common';
import { useAppointmentsChanged } from './realtime';
import { useLockPolicy } from './appointmentLock';
import { useSchedulingActions } from './SchedulingActions';

export const SCR_STAFF_300 = 'SCR-STAFF-300';

const PX_PER_MINUTE = 1.4;
const CELL_MINUTES = 30;
const MINUTE = 60_000;

type By = 'provider' | 'room';
type CalendarView = 'day' | 'week' | 'month' | 'list';

function isCalendarView(value: string | null | undefined): value is CalendarView {
  return value === 'day' || value === 'week' || value === 'month' || value === 'list';
}

function weekDates(anchor: string): string[] {
  const weekday = weekdayOf(anchor);
  const start = addDays(anchor, weekday === 6 ? 0 : -(weekday + 1));
  return Array.from({ length: 7 }, (_, index) => addDays(start, index));
}

function shiftMonth(businessDate: string, delta: number): string {
  const [year, month] = businessDate.split('-').map(Number) as [number, number];
  const value = new Date(Date.UTC(year, month - 1 + delta, 1));
  return value.toISOString().slice(0, 10);
}

function byDate(primary: CalendarMonthView | undefined, extra: CalendarMonthView | undefined): Map<string, CalendarMonthDay> {
  return new Map([...(primary?.days ?? []), ...(extra?.days ?? [])].map((day) => [day.date, day]));
}

function emptyDay(date: string): CalendarMonthDay {
  return { date, activeCount: 0, isHoliday: false, isLeave: false, isBulkClosure: false, isToday: false };
}

function MonthBoard({
  loading,
  error,
  onRetry,
  days,
  layout,
  labelDate,
  onOpen,
}: {
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  days: readonly CalendarMonthDay[];
  layout: 'grid' | 'list';
  labelDate: (date: string) => string;
  onOpen: (date: string) => void;
}) {
  const { t } = useTranslation('sch');
  if (loading) {
    return <LoadingState rows={4} />;
  }
  if (error) {
    return <ErrorState error={error} onRetry={onRetry} />;
  }
  const flags = (day: CalendarMonthDay) =>
    [
      day.isHoliday ? t('sch:calendar.holiday') : null,
      day.isLeave ? t('sch:calendar.unavailable_leave') : null,
      day.isBulkClosure ? t('sch:calendar.bulk_closure') : null,
    ]
      .filter((flag) => flag !== null)
      .join(' · ');
  if (layout === 'list') {
    return (
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }} data-testid="calendar-list">
        {days.map((day) => (
          <li key={day.date}>
            <Button fullWidth variant="outlined" onClick={() => onOpen(day.date)} data-testid="calendar-day" data-date={day.date}>
              {labelDate(day.date)} · {t('sch:calendar.day_count', { count: day.activeCount })}
              {flags(day) ? ` · ${flags(day)}` : ''}
            </Button>
          </li>
        ))}
      </Stack>
    );
  }
  const leading = days[0] ? (IRANIAN_WEEK.indexOf(weekdayOf(days[0].date)) + 7) % 7 : 0;
  return (
    <Box
      data-testid="calendar-month"
      sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 1 }}
      role="group"
      aria-label={t('sch:calendar.month_label', { month: days[0] ? labelDate(days[0].date) : '' })}
    >
      {IRANIAN_WEEK.map((weekday) => (
        <Typography key={weekday} variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          {t(`sch:weekday.${weekday}`)}
        </Typography>
      ))}
      {Array.from({ length: leading }, (_, index) => (
        <Box key={`pad-${index}`} />
      ))}
      {days.map((day) => (
        <ButtonBase
          key={day.date}
          onClick={() => onOpen(day.date)}
          draggable={false}
          data-testid="calendar-day"
          data-date={day.date}
          data-today={day.isToday ? 'true' : 'false'}
          sx={{
            minHeight: 88,
            p: 1,
            border: 1,
            borderColor: day.isToday ? 'primary.main' : 'divider',
            borderRadius: 1,
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            flexDirection: 'column',
          }}
        >
          <Typography variant="body2" fontWeight={day.isToday ? 700 : 400}>
            {labelDate(day.date)}
          </Typography>
          <Typography variant="caption">{t('sch:calendar.day_count', { count: day.activeCount })}</Typography>
          {flags(day) && (
            <Typography variant="caption" color="text.secondary">
              {flags(day)}
            </Typography>
          )}
        </ButtonBase>
      ))}
    </Box>
  );
}

export function CalendarPage() {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const scheduling = useSchedulingActions();
  const lock = useLockPolicy();
  const { branchId } = useActiveBranch();
  const [params, setParams] = useSearchParams();
  const by: By = params.get('by') === 'room' ? 'room' : 'provider';
  const date = fromUrlDate(params.get('date'));
  const resourceId = params.get('resource');
  const preferences = useQuery({
    queryKey: ['identity', session?.tenantId, 'calendar-preferences'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/identity/me/calendar-preferences')),
    enabled: session?.tenantId !== undefined,
    staleTime: 60_000,
  });
  const urlView = params.get('view');
  const viewMode: CalendarView = isCalendarView(urlView)
    ? urlView
    : isCalendarView(preferences.data?.defaultView)
      ? preferences.data.defaultView
      : 'day';
  const gregorian = preferences.data?.calendarSystem === 'Gregorian';
  const labelDate = (businessDate: string) => (gregorian ? toGregorian(businessDate) : formatDay(businessDate));

  const calendar = useQuery({
    queryKey: schKeys.calendar(session?.tenantId, branchId, date, by),
    queryFn: (): Promise<CalendarDayView> =>
      unwrap(
        tenantApi.GET('/api/v1/scheduling/calendar', {
          params: { query: { view: 'day', by, branchId: branchId ?? undefined, date: date ?? undefined } },
        }),
      ),
    enabled: branchId !== null && viewMode === 'day',
    refetchInterval: 60_000,
  });
  const view = calendar.data;
  const monthKey = date?.slice(0, 7);
  const month = useQuery({
    queryKey: ['scheduling', session?.tenantId, 'calendar-month', branchId, monthKey, by, resourceId],
    queryFn: (): Promise<CalendarMonthView> =>
      unwrap(
        tenantApi.GET('/api/v1/scheduling/calendar:month', {
          params: {
            query: {
              branchId: branchId ?? undefined,
              month: monthKey,
              resourceType: resourceId ? by : 'branch',
              resourceId: resourceId ?? undefined,
            },
          },
        }),
      ),
    enabled: branchId !== null && viewMode !== 'day',
    refetchInterval: 60_000,
  });
  const anchor = date ?? month.data?.days.find((day) => day.isToday)?.date ?? month.data?.days[0]?.date;
  const week = anchor ? weekDates(anchor) : [];
  const extraMonth = viewMode === 'week' ? week.map((day) => day.slice(0, 7)).find((value) => value !== anchor?.slice(0, 7)) : undefined;
  const adjacent = useQuery({
    queryKey: ['scheduling', session?.tenantId, 'calendar-month', branchId, extraMonth, by, resourceId],
    queryFn: (): Promise<CalendarMonthView> =>
      unwrap(
        tenantApi.GET('/api/v1/scheduling/calendar:month', {
          params: {
            query: {
              branchId: branchId ?? undefined,
              month: extraMonth,
              resourceType: resourceId ? by : 'branch',
              resourceId: resourceId ?? undefined,
            },
          },
        }),
      ),
    enabled: branchId !== null && extraMonth !== undefined,
  });
  const options = useBookingOptions(branchId, null, viewMode !== 'day');
  const resources = by === 'room' ? (options.data?.rooms ?? []) : (options.data?.providers ?? []);

  useAppointmentsChanged((message) => {
    if (message.branchId === branchId) {
      void queryClient.invalidateQueries({ queryKey: ['scheduling', session?.tenantId, 'calendar'] });
      void queryClient.invalidateQueries({ queryKey: ['scheduling', session?.tenantId, 'calendar-month'] });
    }
  });

  const update = (changes: Record<string, string | null>, nextView: CalendarView = viewMode) => {
    const copy = new URLSearchParams(params);
    copy.set('view', nextView);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null) {
        copy.delete(key);
      } else {
        copy.set(key, value);
      }
    }
    setParams(copy, { replace: true });
  };

  const dayStart = view ? new Date(view.dayStart).getTime() : 0;
  const dayEnd = view ? new Date(view.dayEnd).getTime() : 0;
  const totalMinutes = view ? Math.max((dayEnd - dayStart) / MINUTE, 60) : 0;
  const height = totalMinutes * PX_PER_MINUTE;
  const top = (instant: string) => ((new Date(instant).getTime() - dayStart) / MINUTE) * PX_PER_MINUTE;
  const span = (start: string, end: string) =>
    Math.max(((new Date(end).getTime() - new Date(start).getTime()) / MINUTE) * PX_PER_MINUTE, 18);
  const cells = view ? Array.from({ length: Math.floor(totalMinutes / CELL_MINUTES) }, (_, index) => index) : [];
  const hours = view ? Array.from({ length: Math.floor(totalMinutes / 60) + 1 }, (_, index) => dayStart + index * 60 * MINUTE) : [];

  const blocked = (columnId: string, startMs: number, endMs: number) =>
    (view?.unavailable ?? []).some(
      (block) =>
        block.columnId === columnId &&
        new Date(block.start).getTime() < endMs &&
        new Date(block.end).getTime() > startMs,
    );

  return (
    <Stack data-screen-id={SCR_STAFF_300} spacing={2}>
      <PageHeader titleKey="sch:calendar.title" />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title={t(viewMode === 'day' ? 'sch:calendar.prev_day' : 'sch:calendar.prev')}>
            <span>
              <IconButton
                aria-label={t(viewMode === 'day' ? 'sch:calendar.prev_day' : 'sch:calendar.prev')}
                disabled={viewMode === 'day' ? !view : !anchor}
                onClick={() => {
                  if (viewMode === 'day' && view) {
                    update({ date: toUrlDate(addDays(view.date, -1)) });
                  } else if (anchor) {
                    update({ date: toUrlDate(viewMode === 'week' ? addDays(anchor, -7) : shiftMonth(anchor, -1)) });
                  }
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button size="small" onClick={() => update({ date: null })}>
            {t('sch:calendar.today')}
          </Button>
          <Tooltip title={t(viewMode === 'day' ? 'sch:calendar.next_day' : 'sch:calendar.next')}>
            <span>
              <IconButton
                aria-label={t(viewMode === 'day' ? 'sch:calendar.next_day' : 'sch:calendar.next')}
                disabled={viewMode === 'day' ? !view : !anchor}
                onClick={() => {
                  if (viewMode === 'day' && view) {
                    update({ date: toUrlDate(addDays(view.date, 1)) });
                  } else if (anchor) {
                    update({ date: toUrlDate(viewMode === 'week' ? addDays(anchor, 7) : shiftMonth(anchor, 1)) });
                  }
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>
          {(viewMode === 'day' ? view?.date : anchor) && (
            <Typography variant="subtitle1" component="p" sx={{ mx: 1 }} data-testid="calendar-date">
              {labelDate((viewMode === 'day' ? view?.date : anchor) ?? '')}
            </Typography>
          )}
          {viewMode === 'day' && view?.isHoliday && (
            <Typography variant="body2" color="error" component="span">
              {t('sch:calendar.holiday')}
            </Typography>
          )}
        </Stack>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={viewMode}
          aria-label={t('sch:calendar.title')}
          onChange={(_event, next: CalendarView | null) => next && update({}, next)}
        >
          {(['day', 'week', 'month', 'list'] as const).map((item) => (
            <ToggleButton key={item} value={item} data-testid={`calendar-view-${item}`}>
              {t(`sch:calendar.view_${item}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box sx={{ flexGrow: 1 }} />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={by}
          aria-label={t('sch:calendar.by')}
          onChange={(_event, next: By | null) => next && update({ by: next })}
        >
          <ToggleButton value="provider" data-testid="calendar-by-provider">
            {t('sch:calendar.by_provider')}
          </ToggleButton>
          <ToggleButton value="room" data-testid="calendar-by-room">
            {t('sch:calendar.by_room')}
          </ToggleButton>
        </ToggleButtonGroup>
        {viewMode !== 'day' && (
          <TextField
            select
            size="small"
            label={t('sch:calendar.resource')}
            value={resourceId ?? ''}
            onChange={(event) => update({ resource: event.target.value || null })}
            sx={{ minWidth: 180 }}
            data-testid="calendar-resource"
          >
            <MenuItem value="">{t('sch:calendar.resource_all')}</MenuItem>
            {resources.map((resource) => (
              <MenuItem key={resource.id} value={resource.id}>
                {resource.name ?? ''}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>

      {branchId === null ? (
        <BranchRequired />
      ) : viewMode !== 'day' ? (
        <MonthBoard
          loading={month.isPending || (extraMonth !== undefined && adjacent.isPending)}
          error={month.error ?? adjacent.error}
          onRetry={() => {
            void month.refetch();
            void adjacent.refetch();
          }}
          days={
            viewMode === 'week'
              ? week.map((day) => byDate(month.data, adjacent.data).get(day) ?? emptyDay(day))
              : (month.data?.days ?? [])
          }
          layout={viewMode === 'list' ? 'list' : 'grid'}
          labelDate={labelDate}
          onOpen={(day) => update({ date: toUrlDate(day) }, 'day')}
        />
      ) : calendar.isPending ? (
        <LoadingState rows={6} />
      ) : calendar.error ? (
        <ErrorState error={calendar.error} onRetry={() => void calendar.refetch()} />
      ) : !view || view.columns.length === 0 ? (
        <Alert severity="info">{t('sch:calendar.empty_columns')}</Alert>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Box
            role="region"
            aria-label={t('sch:calendar.grid_label', { date: formatDay(view.date) })}
            sx={{ display: 'flex', minWidth: 120 + view.columns.length * 160 }}
            data-testid="calendar-grid"
          >
            {/* ستون ساعت */}
            <Box sx={{ width: 64, flexShrink: 0, borderInlineEnd: 1, borderColor: 'divider' }}>
              <Box sx={{ height: 40 }} />
              <Box sx={{ position: 'relative', height }}>
                {hours.map((hour) => (
                  <Typography
                    key={hour}
                    variant="caption"
                    color="text.secondary"
                    sx={{ position: 'absolute', top: ((hour - dayStart) / MINUTE) * PX_PER_MINUTE - 8, insetInlineStart: 8 }}
                  >
                    {formatTime(new Date(hour).toISOString(), view.timeZone)}
                  </Typography>
                ))}
              </Box>
            </Box>
            {view.columns.map((column) => (
              <Box
                key={column.id}
                role="group"
                aria-label={column.name ?? undefined}
                sx={{ flex: 1, minWidth: 160, borderInlineEnd: 1, borderColor: 'divider' }}
                data-testid="calendar-column"
                data-column-id={column.id}
              >
                <Box
                  sx={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: 1, borderColor: 'divider' }}
                >
                  <Typography variant="subtitle2" noWrap>
                    {column.name}
                  </Typography>
                </Box>
                <Box sx={{ position: 'relative', height }}>
                  {/* BR-SCH-126: نوار نوع زمان پشت زمان‌ها؛ رنگ تهی = رنگ معمول تقویم. */}
                  {(view.periods ?? [])
                    .filter((period) => period.columnId === column.id)
                    .map((period) => {
                      const color = blockColor(period.timeType, period.color);
                      return color === null ? null : (
                        <Box
                          key={`${period.start}-${period.timeType}`}
                          aria-hidden
                          data-testid="calendar-period"
                          data-time-type={period.timeType}
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            top: Math.max(top(period.start), 0),
                            height: Math.max(Math.min(top(period.end), height) - Math.max(top(period.start), 0), 0),
                            bgcolor: alpha(color, 0.12),
                            borderInlineStart: `3px solid ${color}`,
                            pointerEvents: 'none',
                          }}
                        />
                      );
                    })}
                  {cells.map((cell) => {
                    const startMs = dayStart + cell * CELL_MINUTES * MINUTE;
                    const endMs = startMs + CELL_MINUTES * MINUTE;
                    const iso = new Date(startMs).toISOString();
                    const disabled = !scheduling?.canBook || blocked(column.id, startMs, endMs) || startMs < Date.now();
                    return (
                      <ButtonBase
                        key={cell}
                        disabled={disabled}
                        aria-label={t('sch:calendar.free_slot', { time: formatTime(iso, view.timeZone), column: column.name ?? '' })}
                        onClick={() =>
                          scheduling?.openBooking({
                            date: view.date,
                            start: iso,
                            providerId: by === 'provider' ? column.id : null,
                            roomId: by === 'room' ? column.id : null,
                          })
                        }
                        data-testid="calendar-cell"
                        data-start={iso}
                        sx={{
                          position: 'absolute',
                          insetInline: 0,
                          top: cell * CELL_MINUTES * PX_PER_MINUTE,
                          height: CELL_MINUTES * PX_PER_MINUTE,
                          borderBottom: '1px dashed',
                          borderColor: 'divider',
                          '&:hover': { bgcolor: 'action.hover' },
                          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
                        }}
                      />
                    );
                  })}
                  {view.unavailable
                    .filter((block) => block.columnId === column.id)
                    .map((block) => (
                      <Box
                        key={`${block.start}-${block.kind}`}
                        aria-hidden
                        data-testid="calendar-unavailable"
                        data-kind={block.kind}
                        title={t(`sch:calendar.unavailable_${block.kind}`)}
                        sx={(theme) => ({
                          position: 'absolute',
                          insetInline: 0,
                          top: Math.max(top(block.start), 0),
                          height: Math.max(Math.min(top(block.end), height) - Math.max(top(block.start), 0), 0),
                          bgcolor: alpha(theme.palette.text.disabled, block.kind === 'off' ? 0.12 : 0.22),
                          pointerEvents: 'none',
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'center',
                        })}
                      >
                        {block.kind !== 'off' && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            {t(`sch:calendar.unavailable_${block.kind}`)}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  {view.appointments
                    .filter((appointment) => appointment.columnId === column.id)
                    .map((appointment) => {
                      const color = statusColors[statusTone(appointment.status)];
                      const locked = lock.isLocked(appointment.status);
                      return (
                        <ButtonBase
                          key={appointment.appointmentId}
                          onClick={() => void navigate(appointmentPath(appointment.appointmentId))}
                          draggable={false}
                          data-testid="calendar-appointment"
                          data-locked={locked ? 'true' : 'false'}
                          data-appointment-id={appointment.appointmentId}
                          aria-label={`${formatTime(appointment.start, view.timeZone)} ${appointment.patientName ?? ''} ${t(`sch:status.${appointment.status}`)}`}
                          sx={{
                            position: 'absolute',
                            insetInline: 4,
                            top: top(appointment.start),
                            height: span(appointment.start, appointment.end),
                            bgcolor: alpha(color, 0.14),
                            borderInlineStart: `4px solid ${color}`,
                            borderRadius: 1,
                            px: 1,
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start',
                            overflow: 'hidden',
                            textAlign: 'start',
                            zIndex: 1,
                          }}
                        >
                          <Stack spacing={0} sx={{ width: '100%', minWidth: 0 }}>
                            <Typography variant="caption" fontWeight={600} noWrap>
                              {formatTime(appointment.start, view.timeZone)} · {appointment.patientName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {appointment.serviceMasked ? t('sch:day.masked_service') : appointment.serviceName}
                              {locked ? ` · ${t('sch:calendar.locked')}` : ''}
                            </Typography>
                            {appointment.depositState ? (
                              <Box sx={{ mt: 0.25 }} onClick={(event) => event.stopPropagation()}>
                                <DepositPaymentChip state={appointment.depositState} dueAt={appointment.depositDueAt} timeZone={view.timeZone} />
                              </Box>
                            ) : null}
                          </Stack>
                        </ButtonBase>
                      );
                    })}
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Stack>
  );
}
